import { forwardRef, Fragment } from 'react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { FAMILY_CARD_FONT_FAMILY } from '../family-card-form/familyCardFont';
import { useFitFontSize, VALUE_INK_SHADOW } from '../shared/formFieldEmphasis';
import {
    buildSpecialInformationSheet,
    combDigits,
    toBengaliDigits,
} from './specialInformationFormEngine';
import {
    BASELINE_RATIO,
    CELL_BASELINE_RATIO,
    CELL_VALUE_TYPE,
    FORM_NO_BOX,
    LEADER,
    LINE_BOX,
    PAGE1_COMBS,
    PAGE1_HEAD,
    PAGE1_ROWS,
    PAGE2_COMBS,
    PAGE2_FOOT,
    PAGE2_ROWS,
    PAGE_HEIGHT,
    PAGE_WIDTH,
    RESIDENCE_BOXES,
    TYPE,
    VALUE_INSET,
    VALUE_LIFT,
    VALUE_TYPE,
} from './specialInformationMetrics';

export const SPECIAL_INFORMATION_PAGE_WIDTH = PAGE_WIDTH;
export const SPECIAL_INFORMATION_PAGE_HEIGHT = PAGE_HEIGHT;

const LEADER_TEXT = LEADER.char.repeat(LEADER.count);

const PAGE_STYLE = {
    position: 'relative',
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    background: '#fff',
    color: '#111',
    boxSizing: 'border-box',
    fontFamily: FAMILY_CARD_FONT_FAMILY,
    fontSize: TYPE.body.size,
    overflow: 'hidden',
};

/** A measured baseline becomes the CSS top of the line box. */
const topFor = (baseline, size) => baseline - size * BASELINE_RATIO;

/**
 * Rows are placed by baseline. A line box 1.5 em tall leaves room for dots and
 * descenders inside the hidden overflow — padding would do the same in the
 * browser, but html2canvas shifts the text by it, so the export drifted down.
 */
const textStyle = (size, weight) => ({
    position: 'absolute',
    fontSize: size,
    fontWeight: weight,
    lineHeight: LINE_BOX,
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
});

/** Printed text of the form, anchored by baseline and by left edge. */
function Line({ y, x, size = TYPE.body.size, weight = TYPE.body.weight, children }) {
    return (
        <span style={{ ...textStyle(size, weight), top: topFor(y, size), left: x }}>
            {children}
        </span>
    );
}

/**
 * One printed leader: real dots, clipped to the span the form gives it. They
 * are set in `LEADER.size` rather than the body size — see the metrics for why
 * the dots are drawn larger than the text they follow.
 */
function Leader({ y, from, to, size = LEADER.size }) {
    return (
        <span
            aria-hidden
            data-leader
            style={{
                ...textStyle(size, TYPE.body.weight),
                top: topFor(y, size),
                left: from,
                width: Math.max(0, to - from),
                overflow: 'hidden',
                letterSpacing: LEADER.letterSpacing,
            }}
        >
            {LEADER_TEXT}
        </span>
    );
}

/**
 * A typed answer, sitting just above its dotted line and clipped to it. It
 * prints bigger and heavier than the form's own text, and drops back toward the
 * body size only when the blank is too short for the answer.
 */
function Value({ y, from, to, children }) {
    const maxWidth = Math.max(0, to - from - VALUE_INSET - 2);
    const [ref, size] = useFitFontSize(VALUE_TYPE.size, VALUE_TYPE.minSize, [children, maxWidth]);

    if (!children) return null;
    return (
        <span
            ref={ref}
            data-field-value
            data-filled="true"
            style={{
                ...textStyle(size, VALUE_TYPE.weight),
                top: topFor(y - VALUE_LIFT, size),
                // A little air after the printed label, like a written form.
                left: from + VALUE_INSET,
                maxWidth,
                textShadow: VALUE_INK_SHADOW,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {children}
        </span>
    );
}

/**
 * One combed row: the boxes the form prints between its table rules, and the
 * digits that go in them.
 *
 * The digits are drawn the same way as every other line — absolutely placed on
 * a baseline — because html2canvas does not centre flex children, which pushed
 * them to the bottom of the boxes on export.
 */
function Comb({ comb, value }) {
    const { edges, top, height, tick } = comb;
    const cells = edges.length - 1;
    const text = tick ? (value ? '✓' : '') : combDigits(value, cells);
    const baseline = top + height * CELL_BASELINE_RATIO;

    return (
        <>
            {Array.from({ length: cells }, (_, index) => (
                <span
                    // eslint-disable-next-line react/no-array-index-key
                    key={`box-${index}`}
                    aria-hidden
                    style={{
                        position: 'absolute',
                        left: edges[index],
                        top,
                        width: edges[index + 1] - edges[index],
                        height,
                        border: '1px solid #111',
                        // Neighbours share a rule, the way the printed table does.
                        borderLeftWidth: index === 0 ? 1 : 0,
                        boxSizing: 'border-box',
                    }}
                />
            ))}
            {Array.from({ length: cells }, (_, index) => {
                const char = text[index];
                if (!char) return null;
                return (
                    <span
                        // eslint-disable-next-line react/no-array-index-key
                        key={`digit-${index}`}
                        data-field-value
                        data-filled="true"
                        style={{
                            ...textStyle(CELL_VALUE_TYPE.size, CELL_VALUE_TYPE.weight),
                            textShadow: VALUE_INK_SHADOW,
                            top: topFor(baseline, CELL_VALUE_TYPE.size),
                            // Centred by width, not by transform: html2canvas
                            // drops transforms and the digits slid off centre.
                            left: edges[index],
                            width: edges[index + 1] - edges[index],
                            textAlign: 'center',
                        }}
                    >
                        {char}
                    </span>
                );
            })}
        </>
    );
}

/**
 * The ফরম-২ number box: one plain rectangle rather than a comb, so the number
 * prints across it as free text instead of one digit per cell.
 */
function FormNoBox({ value }) {
    const { top, height, left, right } = FORM_NO_BOX;
    // The writing room inside the rules, which is both what the number is fitted
    // to and what `FORM_NO_BOX.maxLength` is counted from.
    const maxWidth = right - left - VALUE_INSET * 2;
    const digits = toBengaliDigits(value);
    const [ref, size] = useFitFontSize(VALUE_TYPE.size, VALUE_TYPE.minSize, [digits, maxWidth]);

    return (
        <>
            <span
                aria-hidden
                style={{
                    position: 'absolute',
                    left,
                    top,
                    width: right - left,
                    height,
                    border: '1px solid #111',
                    boxSizing: 'border-box',
                }}
            />
            {digits ? (
                <span
                    ref={ref}
                    data-field-value
                    data-filled="true"
                    style={{
                        ...textStyle(size, VALUE_TYPE.weight),
                        textShadow: VALUE_INK_SHADOW,
                        top: topFor(top + height * CELL_BASELINE_RATIO, size),
                        left: left + VALUE_INSET,
                        width: maxWidth,
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {digits}
                </span>
            ) : null}
        </>
    );
}

/** The solid rules the committee signs above. */
function Rule({ y, from, to }) {
    return (
        <span
            aria-hidden
            style={{
                position: 'absolute',
                left: from,
                top: y,
                width: Math.max(0, to - from),
                height: 0,
                borderTop: '1px solid #111',
            }}
        />
    );
}

/** Label, leader and value for every row on a page. */
function Rows({ rows, t, d }) {
    return rows.map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={`${row.y}-${row.key || row.value || index}`}>
            {row.key ? (
                <Line y={row.y} x={row.x}>{t(`specialInformationForm.preview.${row.key}`)}</Line>
            ) : null}
            {row.from != null ? <Leader y={row.y} from={row.from} to={row.to} /> : null}
            {row.value ? (
                // `valueTo` where the answer must stop before the dots do — see
                // the metrics: a few blanks run under the boxes of the row above.
                <Value y={row.y} from={row.from} to={row.valueTo ?? row.to}>{d[row.value]}</Value>
            ) : null}
        </Fragment>
    ));
}

function PageOne({ d, t }) {
    return (
        <div data-special-information-page style={PAGE_STYLE}>
            <Line
                y={PAGE1_HEAD.title.y}
                x={PAGE1_HEAD.title.x}
                size={TYPE.title.size}
                weight={TYPE.title.weight}
            >
                {t('specialInformationForm.preview.title')}
            </Line>
            <Line y={PAGE1_HEAD.formNoLabel.y} x={PAGE1_HEAD.formNoLabel.x}>
                {t('specialInformationForm.preview.formNoLabel')}
            </Line>
            <FormNoBox value={d.formNo} />

            {/* Item ৯'s tenure ticks close page 1, the way the form prints them. */}
            {RESIDENCE_BOXES.map((box) => (
                <Comb
                    key={box.choice}
                    comb={{ top: box.top, height: box.height, edges: [box.left, box.left + box.width], tick: true }}
                    value={d.residenceType === box.choice}
                />
            ))}

            {PAGE1_COMBS.map((comb) => (
                <Comb key={comb.value} comb={comb} value={d[comb.value]} />
            ))}
            <Rows rows={PAGE1_ROWS} t={t} d={d} />
        </div>
    );
}

function PageTwo({ d, t }) {
    return (
        <div data-special-information-page style={PAGE_STYLE}>
            {PAGE2_COMBS.map((comb) => (
                <Comb key={comb.value} comb={comb} value={d[comb.value]} />
            ))}
            <Rows rows={PAGE2_ROWS} t={t} d={d} />

            {PAGE2_FOOT.rules.map((rule) => (
                <Rule key={rule.from} y={rule.y} from={rule.from} to={rule.to} />
            ))}
        </div>
    );
}

function SpecialInformationFormPreview({ form }, ref) {
    const { t } = useTranslation();
    const d = buildSpecialInformationSheet(form);

    return (
        <div
            ref={ref}
            data-special-information-document
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
            <PageOne d={d} t={t} />
            <PageTwo d={d} t={t} />
        </div>
    );
}

export default forwardRef(SpecialInformationFormPreview);
